import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, ListChecks, X } from 'lucide-react';
import { Button } from '../../components/ui';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { useChecklists } from '../checklists/api';

export interface ChecklistPickerProps {
  projectCode: string;
  value: string[];
  onChange: (checklistIds: string[]) => void;
  id?: string;
  /** Lets a closed dialog mount the field without fetching the list. */
  enabled?: boolean;
}

/**
 * Multi-select for the checklists a run is assembled from. One release pass
 * usually covers more than one list, so the run takes several and keeps them
 * in the order they were picked; the chosen ones are listed under the field
 * rather than squeezed into it.
 */
export const ChecklistPicker = ({
  projectCode,
  value,
  onChange,
  id,
  enabled = true,
}: ChecklistPickerProps) => {
  const { t } = useT();
  const { data } = useChecklists(projectCode, { perPage: 100 }, { enabled });

  // A checklist with no items has nothing to run.
  const runnable = (data?.data ?? []).filter((checklist) => checklist.itemCount > 0);
  const chosen = value
    .map((checklistId) => runnable.find((checklist) => checklist.id === checklistId))
    .filter((checklist): checklist is (typeof runnable)[number] => checklist !== undefined);

  const toggle = (checklistId: string): void => {
    onChange(
      value.includes(checklistId)
        ? value.filter((candidate) => candidate !== checklistId)
        : [...value, checklistId],
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <RadixMenu.Root>
        <RadixMenu.Trigger asChild>
          <button
            id={id}
            type="button"
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-md border border-border bg-surface px-2.5',
              'text-left text-[14px] transition-colors hover:border-border-strong',
            )}
          >
            <ListChecks className="size-3.5 shrink-0 text-ink-subtle" />
            <span className={chosen.length === 0 ? 'text-ink-subtle' : 'text-ink'}>
              {chosen.length === 0
                ? t('runs.fieldChecklistPlaceholder')
                : t('runs.checklistsChosen', { count: chosen.length })}
            </span>
            <ChevronDown className="ml-auto size-4 shrink-0 text-ink-subtle" />
          </button>
        </RadixMenu.Trigger>

        <RadixMenu.Portal>
          <RadixMenu.Content
            align="start"
            side="bottom"
            sideOffset={4}
            collisionPadding={8}
            className="z-50 max-h-80 w-[min(28rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg"
          >
            {runnable.length === 0 ? (
              <p className="px-2 py-1.5 text-[12px] text-ink-subtle">
                {t('runs.noRunnableChecklist')}
              </p>
            ) : (
              runnable.map((checklist) => (
                <RadixMenu.CheckboxItem
                  key={checklist.id}
                  checked={value.includes(checklist.id)}
                  onCheckedChange={() => toggle(checklist.id)}
                  onSelect={(event) => event.preventDefault()}
                  className={cn(
                    'relative flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2',
                    'text-[13px] text-ink outline-none data-[highlighted]:bg-sunken',
                  )}
                >
                  <RadixMenu.ItemIndicator className="absolute left-2 text-accent-fg">
                    <Check className="size-3.5" />
                  </RadixMenu.ItemIndicator>
                  <span className="truncate">
                    {t('runs.fieldChecklistOption', {
                      key: checklist.checklistKey,
                      title: checklist.title,
                      count: checklist.itemCount,
                    })}
                  </span>
                </RadixMenu.CheckboxItem>
              ))
            )}
          </RadixMenu.Content>
        </RadixMenu.Portal>
      </RadixMenu.Root>

      {chosen.length > 0 ? (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {chosen.map((checklist, index) => (
            <li key={checklist.id} className="flex items-center gap-2 px-2.5 py-1.5">
              <span className="w-5 shrink-0 text-[12px] tabular-nums text-ink-subtle">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                {t('runs.fieldChecklistOption', {
                  key: checklist.checklistKey,
                  title: checklist.title,
                  count: checklist.itemCount,
                })}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => toggle(checklist.id)}
                aria-label={t('runs.removeChecklist', { title: checklist.title })}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
